/**
 * User API Service
 * Handles user profile and preferences API operations
 */

import { apiClient } from '../apiClient';
import { UserProfile, UserMaxes } from '@/types/workout';

// Request/Response types
export interface CreateUserProfileRequest {
  name: string;
  sex?: 'male' | 'female';
  competitionDate?: string;
  startDate: string;
  preferences: UserProfile['preferences'];
  healthProfile?: any;
  currentMaxes?: UserMaxes;
}

export interface UpdateUserProfileRequest {
  name?: string;
  sex?: 'male' | 'female';
  competitionDate?: string;
  startDate?: string;
  preferences?: Partial<UserProfile['preferences']>;
  healthProfile?: any;
  currentMaxes?: UserMaxes;
}

export interface UserProfileResponse extends UserProfile {
  userMaxes?: UserMaxes;
}

// Health profile interface
export interface HealthProfile {
  weight?: number;
  height?: number;
  age?: number;
  medicalConditions?: string[];
  injuries?: string[];
  fitnessLevel?: 'beginner' | 'intermediate' | 'advanced' | 'elite';
  goals?: string[];
}

/**
 * User API Service Class
 */
class UserApiService {
  /**
   * Create user profile
   */
  async createProfile(data: CreateUserProfileRequest): Promise<UserProfile> {
    return apiClient.post('/users/profile', data);
  }

  /**
   * Get user profile
   */
  async getProfile(): Promise<UserProfileResponse> {
    return apiClient.get('/users/profile');
  }

  /**
   * Update user profile
   */
  async updateProfile(data: UpdateUserProfileRequest): Promise<UserProfile> {
    return apiClient.patch('/users/profile', data);
  }

  /**
   * Delete user profile
   */
  async deleteProfile(): Promise<void> {
    return apiClient.delete('/users/profile');
  }

  /**
   * Get user maxes
   */
  async getUserMaxes(): Promise<UserMaxes> {
    return apiClient.get('/users/maxes');
  }

  /**
   * Update user maxes
   */
  async updateUserMaxes(maxes: UserMaxes): Promise<UserMaxes> {
    return apiClient.put('/users/maxes', maxes);
  }

  /**
   * Get user preferences
   */
  async getPreferences(): Promise<UserProfile['preferences']> {
    return apiClient.get('/users/preferences');
  }

  /**
   * Update user preferences
   */
  async updatePreferences(preferences: Partial<UserProfile['preferences']>): Promise<UserProfile['preferences']> {
    return apiClient.patch('/users/preferences', preferences);
  }

  /**
   * Get health profile
   */
  async getHealthProfile(): Promise<HealthProfile> {
    return apiClient.get('/users/health-profile');
  }

  /**
   * Update health profile
   */
  async updateHealthProfile(healthProfile: Partial<HealthProfile>): Promise<HealthProfile> {
    return apiClient.patch('/users/health-profile', healthProfile);
  }

  /**
   * Check if user profile exists
   */
  async profileExists(): Promise<{ exists: boolean }> {
    return apiClient.get('/users/profile/exists');
  }

  /**
   * Get user statistics
   */
  async getUserStats(): Promise<any> {
    return apiClient.get('/users/stats');
  }

  /**
   * Export user data
   */
  async exportUserData(): Promise<any> {
    return apiClient.get('/users/export');
  }

  /**
   * Delete user account
   */
  async deleteAccount(): Promise<void> {
    return apiClient.delete('/users/account');
  }
}

export const userApi = new UserApiService();
